import { Component, OnInit } from "@angular/core";
import { SettingsService } from "./shared/api/resources/settings/settings.service";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { GlobalService } from "./shared/global.service";
import { StripeService } from "ngx-stripe";
import { WidgetService } from "./shared/api/widget/widget.service";
import { ToasterService } from "./shared/atoms/toaster/toaster.service";
import { CartService } from "./shared/cart/cart.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [],
})
export class AppComponent implements OnInit {
  currentUrl = "";
  settingsLoaded = false;
  stripeKeyLoaded = false;
  cartAmount = 0;
  navigationHistory = [];
  backLink = "";
  goingBack = false;
  countdown: string;
  showTooltip = false;
  private countdownInterval: any;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _settings: SettingsService,
    private _globalService: GlobalService,
    private _widgetService: WidgetService,
    private _stripeService: StripeService,
    private _toasterService: ToasterService,
    private _cartService: CartService
  ) {
    this._router.events.forEach((item) => {
      if (item instanceof NavigationEnd) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'page_change',
          'currentPage': item.url,
        });
      }
    });
  }

  async ngOnInit() {
    this._route.queryParams.subscribe((param: any) => {
      if (param?.guid && !param?.isAtmPayment) {
        this._globalService.guid = param.guid;

        this._settings.getAllRequests().subscribe(
          () => { },
          (error) => this._toasterService.showError(error.error.message),
          () => (this.settingsLoaded = true)
        );

        this._widgetService.getStripeKey().subscribe(
          (data) => this._stripeService.setKey(data.stripeApiKey),
          (error) => this._toasterService.showError(error.error.message),
          () => (this.stripeKeyLoaded = true)
        );

        this._router.navigate([""], {
          queryParams: { isAtmPayment: null },
          queryParamsHandling: 'merge'
        });
      }

      if (param?.hide_date) {
        this._globalService.hide_date = param.hide_date;
      }

      if (param?.event_date) {
        this._globalService.event_date = param.event_date;
      }

      if (param?.skip_my_cart) {
        this._globalService.skip_my_cart = true;
      }

      if (param?.scid && param?.scguid) {
        if (param?.redirect_status === 'succeeded') {
          this._router.navigate(['../thankyou/'], {
            queryParamsHandling: 'merge'
          }
          );
        } else if (param?.redirect_status === 'failed') {
          this._globalService.errorMessage = 'Something unexpected happened. Please try again from the begining!';

          this._router.navigate(['../error/'], {
            queryParams: {
              goHome: true,
            },
            queryParamsHandling: 'merge'
          });
        }
      }
    });

    this._router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        const path = evt.url.split("?")[0];

        if (!this.goingBack && this.navigationHistory[0] !== this.currentUrl && this.currentUrl !== '/error') {
          this.navigationHistory.unshift(this.currentUrl);
        }

        if (this.goingBack || path === '/error') {
          this.navigationHistory.splice(0, 1);
        }

        this.backLink = this.navigationHistory[0];
        this.currentUrl = path;
        this.goingBack = false;

        document.getElementsByTagName("main").item(0).scrollTop = 0;
      }
    });

    this._cartService.shoppingCartChange.subscribe((shoppingCart) => {
      this.cartAmount = this._cartService.getCartBookingsAmount();
    
      if (shoppingCart?.isExpired) {
        this.handleExpiration();
        return;
      }
    
      const expirationDateUtc = shoppingCart?.shoppingCartExpiration;
    
      if (expirationDateUtc) {
        const localDate = this.convertUtcToLocal(expirationDateUtc);
        this.startCountdown(localDate);
      }
    });

    this._globalService.expirationDate$.subscribe((date) => {
      if (date) {
        this.startCountdown(date);
      }
    });

    this._globalService.timerActive$.subscribe((active) => {
      if (!active) {
        clearInterval(this.countdownInterval);
        this.countdown = null;
      }
    });
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  handleExpiration(): void {
    clearInterval(this.countdownInterval);
    this._globalService.stopTimer();
    this._router.navigate(['../timeExpiration'], { queryParamsHandling: 'merge' });
  }

  convertUtcToLocal(utcString: string): Date {
    return new Date(utcString + 'Z');
  }

  startCountdown(expirationDate: Date): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this._globalService.startTimer();
  
    this.countdownInterval = setInterval(() => {
      const now = new Date();
      const diff = expirationDate.getTime() - now.getTime();
  
      if (diff <= 0) {
        clearInterval(this.countdownInterval);
        this.countdown = 'Expired';
        this._router.navigate(['../timeExpiration'], {queryParamsHandling: 'merge'});
        this.countdown = null;
        this._globalService.stopTimer();
        
        return;
      }
  
      const seconds = Math.floor((diff / 1000) % 60);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
  
      this.countdown = `${minutes}m ${seconds}s`;
    }, 1000);
  }

  preventContextMenu(evt) {
    evt.preventDefault();
    evt.stopPropagation();
  }

  handleBackClick(): void {
    this.goingBack = true;
  }

  public allowNavigation(): boolean {
    const url = this.currentUrl.split("?")[0];
    return (
      !["/", "/home", "/thankyou", "/error", "/timeExpiration", "/multibanco"].includes(url) &&
      !this._globalService.processingPayment &&
      !(url === "/cart" && !this._globalService.event)
    );
  }

  goToCart() {
    if (this.cartAmount > 0) {
      this._router.navigate(["cart"], { queryParamsHandling: "merge" }).then();
    }
  }

  get title(): string {
    let title = "";
    switch (this.currentUrl.split("?")[0]) {
      case "/":
        title = "Booking";
        break;
      case "/home":
        title = "Booking";
        break;
      case "/pax":
        title = "Add Pax";
        break;
      case "/add":
        title = "Add";
        break;
      case "/transport":
        title = "Transport";
        break;
      case "/question":
        title = "Question";
        break;
      case "/summary":
        title = "Summary";
        break;
      case "/cart":
        title = "My Cart";
        break;
      case "/payment":
        title = "Payment";
        break;
      case "/thankyou":
        title = "Thank You";
        break;
      case "/terms":
        title = "Policy";
        break;
      case "/error":
        title = "Error";
        break;
      case "/timeExpiration":
        title = "Time Expired";
        break;
    }
    return title;
  }

  get loading(): boolean {
    return this._globalService.loading;
  }

  public skipMyCart(): any {
    return this._globalService.skip_my_cart;
  }

  public isSkipMyCartAndSummaryRoute() {
    const isSkipMyCart =
      this._globalService.skip_my_cart === true ? true : false;
    const isSummaryRoute = this._router.url.includes("summary");
    return isSummaryRoute && isSkipMyCart === true ? true : false;
  }
}
